/**
 * Static scenery merged by material: every plain mesh under `root` that shares a material (and attribute layout)
 * becomes one mesh. Banners, arches, paddock clutter were one draw per part, times up to four passes (main + three
 * shadow cascades). Materials stay the same objects, so later map swaps (livery, flags) still apply.
 */
import * as THREE from 'three/webgpu';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { toFloat } from './vegetation';

export function mergeStatic(root: THREE.Object3D): number {
  root.updateMatrixWorld(true);
  const inv = root.matrixWorld.clone().invert();
  const groups = new Map<string, { mat: THREE.Material; cast: boolean; receive: boolean; meshes: THREE.Mesh[] }>();
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || (m as THREE.SkinnedMesh).isSkinnedMesh || (m as THREE.InstancedMesh).isInstancedMesh || Array.isArray(m.material)) return;
    if (m.morphTargetInfluences || !m.visible || m.userData.noMerge) return;
    const g = m.geometry as THREE.BufferGeometry;
    const layout = Object.keys(g.attributes).sort().join(',') + (g.index ? '|i' : '');
    const key = `${m.material.uuid}|${layout}|${m.castShadow}|${m.receiveShadow}|${m.layers.mask}|${m.renderOrder}`;
    let e = groups.get(key);
    if (!e) groups.set(key, e = { mat: m.material, cast: m.castShadow, receive: m.receiveShadow, meshes: [] });
    e.meshes.push(m);
  });
  let saved = 0;
  for (const e of groups.values()) {
    if (e.meshes.length < 2) continue;
    const geos = e.meshes.map((m) => {
      const g = toFloat(m.geometry as THREE.BufferGeometry); // plain float copies (quantised / interleaved glTF data)
      g.applyMatrix4(new THREE.Matrix4().multiplyMatrices(inv, m.matrixWorld));
      return g;
    });
    const merged = mergeGeometries(geos, false);
    if (!merged) continue;
    const mesh = new THREE.Mesh(merged, e.mat);
    mesh.castShadow = e.cast; mesh.receiveShadow = e.receive;
    mesh.layers.mask = e.meshes[0]!.layers.mask;
    mesh.renderOrder = e.meshes[0]!.renderOrder;
    mesh.name = `merged:${e.mat.name || e.mat.type}`;
    root.add(mesh);
    for (const m of e.meshes) m.removeFromParent();
    saved += e.meshes.length - 1;
  }
  return saved;
}
